import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Globe2, Shield, Award, Building2, Briefcase, Users, MapPin, Phone, Mail,
  CheckCircle, ArrowRight, ArrowUpRight, FileCheck, ClipboardList, Plane,
  HardHat, Factory, HeartPulse, Hotel, Zap, Wrench, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta } from '@/hooks/useSEO';

/**
 * /international-manpower-recruitment-europe-russia-israel-saudi-arabia
 * ────────────────────────────────────────────────────────────────────────
 * International recruitment corridor PILLAR page.
 * Positions Zafco HRM (Delhi HQ + Mumbai infra + pan-India sourcing) as the
 * Government-of-India-approved bridge between global employers and Indian
 * talent across Saudi Arabia, Europe, Russia, Israel and other markets.
 *
 * Cannibalization guard: targets *international* recruitment intent.
 * Avoids over-optimization on Delhi/India-local recruitment queries which
 * are already served by existing pages.
 */

const PRIMARY_PHONE = '+91-99588-48998';
const PRIMARY_EMAIL = 'info@zafco.in';
const MEA_REG = 'B-0762/DEL/PER/1000+/5/9148/2015';

const REGION_MATRIX = [
  {
    region: 'Saudi Arabia',
    industries: 'Construction · Oil & Gas · Hospitality · Healthcare',
    talent: 'Skilled trades, technicians, engineers, hospitality crews, nurses',
    capability: 'High-volume mobilization · MoFA / Wakala / visa stamping in-house',
  },
  {
    region: 'Europe (Germany · Poland · Romania · Hungary · Czech Republic)',
    industries: 'Manufacturing · Engineering · Warehousing · Hospitality · Skilled Trades',
    talent: 'CNC operators, welders, fitters, electricians, hospitality staff, drivers',
    capability: 'EU work-permit advisory · skill alignment to host-country labour codes',
  },
  {
    region: 'Russia',
    industries: 'Industrial Projects · Infrastructure · Energy · Manufacturing',
    talent: 'Project technicians, mechanical/electrical specialists, plant operators',
    capability: 'Long-cycle project deployment · multi-country site support',
  },
  {
    region: 'Israel',
    industries: 'Construction · Infrastructure · Specialized Technical Services',
    talent: 'Iron-benders, shutter carpenters, plasterers, tile masons, formwork crews',
    capability: 'PIBA-compliant documentation · construction-trade test pipelines',
  },
  {
    region: 'Wider Middle East (UAE · Qatar · Kuwait · Oman · Bahrain)',
    industries: 'Construction · Hospitality · Healthcare · Logistics · Retail',
    talent: 'Full spectrum — unskilled to senior management',
    capability: 'Decade of established corridor experience',
  },
  {
    region: 'Pan-India sourcing network',
    industries: 'All verticals',
    talent: '28 states · 70+ trade-test centres · 2,000+ vocational training partners',
    capability: 'Volume sourcing · regional language coverage · ITI / NSDC certified candidates',
  },
];

const EMPLOYER_PROCESS = [
  { icon: ClipboardList, title: 'Manpower Indent & Requirement Mapping',
    desc: 'Detailed JD intake, country-specific compliance brief, salary benchmarking.' },
  { icon: Users, title: 'Sourcing from Pan-India Talent Pool',
    desc: 'Database, referrals, vocational institute tie-ups, ground-team mobilization.' },
  { icon: FileCheck, title: 'Trade Testing & Multi-Stage Screening',
    desc: 'Practical trade tests, technical interviews, language and behavioural screening.' },
  { icon: Briefcase, title: 'Client Interview Coordination',
    desc: 'In-person, video or panel interviews — anywhere in India, scheduled in 72 hours.' },
  { icon: Shield, title: 'Documentation, Visa & MEA Compliance',
    desc: 'Medical, PCC, attestations, visa stamping, MEA emigration clearance.' },
  { icon: Plane, title: 'Mobilization & Post-Departure Support',
    desc: 'Ticketing, briefing, airport handover, employer-side arrival coordination.' },
];

const CANDIDATE_PROMISES = [
  'Ethical recruitment — zero placement fees from candidates, ever.',
  'Transparent contracts shared in the candidate\'s preferred language.',
  'End-to-end documentation, medical, PCC and visa stamping support.',
  'Authentic, verifiable employer offers — no broker layers.',
  'Pre-departure orientation, country briefings, and welfare support.',
  'Long-term career-progression pathways across multiple corridors.',
];

const VERTICALS = [
  { icon: HardHat,    title: 'Construction & Infrastructure',
    desc: 'High-rise, metro, ports, EPC mega-projects, formwork & finishing trades.' },
  { icon: Factory,    title: 'Industrial & Manufacturing',
    desc: 'CNC operators, welders, fitters, assemblers, QC technicians, plant engineers.' },
  { icon: HeartPulse, title: 'Healthcare & Medical',
    desc: 'Registered nurses, allied-health professionals, hospital support staff.' },
  { icon: Hotel,      title: 'Hospitality & Tourism',
    desc: 'F&B service, kitchen brigade, housekeeping, front office, club staff.' },
  { icon: Zap,        title: 'Energy & Petroleum',
    desc: 'Pipefitters, scaffolders, turnaround/shutdown crews, instrument technicians.' },
  { icon: Wrench,     title: 'Engineering & Technical Services',
    desc: 'Mechanical, electrical, instrumentation engineers; supervisors & site leads.' },
];

const TRUST_BULLETS = [
  { icon: Shield,    label: 'Government of India Approved',  detail: `MEA Reg. ${MEA_REG}` },
  { icon: Award,     label: 'Decade-plus operational track record', detail: '1,000+ workers deployed annually' },
  { icon: Globe2,    label: 'Active corridors in 12+ countries',    detail: 'Saudi Arabia · Europe · Russia · Israel · GCC' },
  { icon: Building2, label: 'Delhi HQ + Mumbai corporate office',   detail: 'Pan-India sourcing infrastructure' },
  { icon: Users,     label: '28-state sourcing reach',               detail: '70+ trade-test centres' },
  { icon: FileCheck, label: 'Full compliance stack',                 detail: 'MEA · MoFA · PIBA · EU work-permit advisory' },
];

const FAQS = [
  {
    q: 'Is Zafco HRM a Government-approved recruitment agency for sending workers from India to Saudi Arabia, Europe, Russia and Israel?',
    a: 'Yes. Zafco Human Resource Management is a Government of India approved overseas recruitment agency registered with the Ministry of External Affairs (Protector General of Emigrants). Our MEA registration certificate is B-0762/DEL/PER/1000+/5/9148/2015. We are authorized to recruit and deploy Indian workers internationally, including to Saudi Arabia, the European Union, Russia, Israel and the wider GCC.',
  },
  {
    q: 'Where is Zafco HRM headquartered and where do you source candidates from?',
    a: 'Our head office is in New Delhi with corporate infrastructure in Mumbai. Our sourcing reach spans 28 Indian states through 70+ trade-test centres and partnerships with 2,000+ vocational training institutes, ITIs and NSDC-certified skilling partners. This pan-India network is what allows us to mobilize 50–500 workers for a single client requirement within 30–90 days.',
  },
  {
    q: 'What industries do you supply manpower for in Saudi Arabia?',
    a: 'For the Saudi Arabian market we have active deployment pipelines in construction and infrastructure (skilled trades, supervisors, project engineers), oil & gas (pipefitters, scaffolders, shutdown crews), hospitality (F&B, housekeeping, kitchen brigade), healthcare (registered nurses, allied health) and industrial manufacturing. We handle MoFA attestation, Wakala documentation and visa stamping in-house at our Delhi office.',
  },
  {
    q: 'Can you supply skilled Indian workforce to European employers?',
    a: 'Yes. We work with employers across Germany, Poland, Romania, Hungary, the Czech Republic and other EU jurisdictions, supplying CNC operators, welders, fitters, electricians, drivers, warehousing staff, hospitality crews and skilled trades. Our team handles work-permit advisory aligned to each host country\'s labour code, German Opportunity Card pathways where applicable, and EU-compliant documentation.',
  },
  {
    q: 'Do you deploy manpower to Russia for industrial and infrastructure projects?',
    a: 'Yes. Russia is an active corridor for long-cycle industrial and infrastructure projects. We supply project technicians, mechanical and electrical specialists, plant operators and skilled fabrication crews. We support multi-country site rotation for clients operating across CIS markets, and our documentation team manages the relevant visa categories end-to-end.',
  },
  {
    q: 'How do you support Israeli construction employers with Indian workforce?',
    a: 'For Israel we focus on the construction-trade corridor — iron-benders, shutter carpenters, plasterers, tile masons and formwork crews. Our recruitment process is aligned to PIBA (Population and Immigration Authority) requirements and includes practical trade tests conducted by qualified assessors before any candidate is shortlisted for client interview.',
  },
  {
    q: 'How long does it take to deploy workers from India to an overseas employer?',
    a: 'Typical timelines: shortlist & interview in 5–10 days, documentation and medical in 15–25 days, visa processing in 15–45 days depending on jurisdiction, mobilization within 7 days of visa receipt. End-to-end deployment for most corridors runs 45–90 days. High-volume mandates with established clients can be compressed where the regulatory pathway permits.',
  },
  {
    q: 'What is the employer hiring process when partnering with Zafco HRM?',
    a: 'Six steps: (1) Manpower Indent and requirement mapping; (2) Pan-India sourcing against your job description; (3) Multi-stage screening including practical trade tests; (4) Client interview coordination in-person, video or panel — anywhere in India; (5) Documentation, medical, visa and MEA emigration clearance; (6) Mobilization, ticketing, briefing and post-departure support. Every step has a single accountable point of contact on our side.',
  },
  {
    q: 'Do you charge candidates a recruitment fee?',
    a: 'No. Zafco HRM operates on an ethical-recruitment model. We are an employer-paid recruitment agency — candidates are never charged a placement fee for jobs we recruit for. Our revenue comes from the client employer per the engagement contract, in line with MEA guidelines and international fair-recruitment standards.',
  },
  {
    q: 'How do you ensure the manpower you supply meets international compliance standards?',
    a: 'Compliance is built into the process, not bolted on. Every candidate undergoes practical trade testing, medical fitness, police verification and document authentication before we issue a confirmed offer. Our recruitment workflow is MEA-aligned, our country-specific desks track local labour-code updates in real time, and we maintain a closed-loop audit on every deployment so that grievance redressal, repatriation support and welfare obligations remain serviceable for the full duration of the contract.',
  },
];

const InternationalManpowerRecruitmentPage = () => {
  usePageMeta(
    'International Manpower Recruitment | Saudi · EU · Russia · Israel',
    'Government of India approved overseas recruitment agency. Skilled Indian workforce for Saudi Arabia, Europe, Russia, Israel. Delhi HQ · MEA-registered.',
    {
      canonical: '/international-manpower-recruitment-europe-russia-israel-saudi-arabia',
      image: '/social-media/og-international-recruitment.jpg',
      imageAlt: 'International Manpower Recruitment — Saudi Arabia · Europe · Russia · Israel — Government of India approved (MEA Registered) overseas recruitment agency.',
    },
  );

  // FAQPage + BreadcrumbList JSON-LD
  useEffect(() => {
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'International Recruitment',
          item: 'https://zafco.in/international-manpower-recruitment-europe-russia-israel-saudi-arabia' },
      ],
    };
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const els = [];
    for (const obj of [breadcrumb, faqSchema]) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-intl-pillar', '1');
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      els.push(s);
    }
    return () => els.forEach((s) => s.remove());
  }, []);

  return (
    <div data-testid="international-manpower-recruitment-page" data-theme="light">
      <PageHeader
        title="Top International Manpower Recruitment Agency for Europe, Russia & Saudi Arabia"
        subtitle="Government of India approved recruitment partner — supplying skilled Indian workforce to global employers across Saudi Arabia, the European Union, Russia, Israel and the wider GCC."
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'International Recruitment', path: '/international-manpower-recruitment-europe-russia-israel-saudi-arabia' },
        ]}
      />

      {/* ── GOI APPROVAL TRUST BAR ───────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-4" data-testid="intl-mea-bar">
        <div className="container mx-auto px-6 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 text-center md:text-left">
          <Shield className="w-8 h-8 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-bold text-base md:text-lg">Government of India Approved Overseas Recruitment Agency</p>
            <p className="text-green-100 text-xs md:text-sm">MEA Registration Certificate: {MEA_REG}</p>
          </div>
          <Award className="w-8 h-8 shrink-0 hidden md:block" aria-hidden="true" />
        </div>
      </div>

      {/* ── SECTION 2: Corporate Overview & Government Compliance ──────── */}
      <section className="py-14 lg:py-20 bg-white" data-testid="intl-overview">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="text-[11px] font-bold tracking-[0.22em] text-zafco-blue uppercase mb-3">Corporate Overview</div>
            <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mb-5 leading-tight">
              The Indian-talent partner for serious international employers
            </h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              <strong>Zafco Human Resource Management</strong> is a Government-of-India approved overseas recruitment
              agency headquartered in New Delhi with corporate infrastructure in Mumbai. We exist for one reason:
              to give international employers reliable, compliant, mobilization-ready access to India\'s skilled
              workforce — at any scale, in any geography where India holds a bilateral labour arrangement.
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Across more than a decade of operations we have built a pan-India sourcing network that spans 28
              states, 70-plus trade-test centres and over 2,000 vocational-training partnerships — and a
              country-desk model that lets us run live deployment pipelines for Saudi Arabia, the European Union,
              Russia, Israel and the wider GCC simultaneously, without dilution of quality on any single corridor.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We are an <strong>employer-paid agency</strong>. Candidates are never charged a placement fee. Every
              mandate is delivered under MEA emigration-clearance compliance, with documented trade testing,
              authenticated documentation and post-deployment welfare obligations honoured for the full contract term.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90">
                <Link to="/contact" data-testid="intl-overview-contact-cta">
                  <Phone className="w-4 h-4 mr-2" /> Speak to our Global Recruitment Desk
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                <a href={`https://wa.me/919958848998`} target="_blank" rel="noopener noreferrer">
                  WhatsApp our team <ArrowUpRight className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 lg:p-8 shadow-sm"
          >
            <div className="text-[10px] font-bold tracking-[0.22em] text-amber-600 uppercase mb-2">Talk to our Employer Desk</div>
            <h3 className="text-xl font-bold text-zafco-blue mb-5">Request a manpower indent or sourcing brief</h3>
            <ConsultationForm variant="light" source="international-recruitment-pillar" />
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 3: Global Recruitment Delivery Matrix ───────────────── */}
      <section className="py-14 lg:py-20 bg-slate-50" data-testid="intl-matrix">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mb-9">
            <div className="text-[11px] font-bold tracking-[0.22em] text-zafco-blue uppercase mb-3">Delivery Matrix</div>
            <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mb-3 leading-tight">
              Global recruitment corridors — at a glance
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Active deployment pipelines, country-desks staffed with regional specialists, and a sourcing engine
              that scales from 5 to 500 workers per indent.
            </p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-zafco-blue text-white">
                <tr>
                  <th className="px-5 py-3 text-left font-semibold">Region</th>
                  <th className="px-5 py-3 text-left font-semibold">Key Industries</th>
                  <th className="px-5 py-3 text-left font-semibold">Talent Categories</th>
                  <th className="px-5 py-3 text-left font-semibold">Recruitment Capability</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {REGION_MATRIX.map((row) => (
                  <tr key={row.region} className="hover:bg-slate-50/60">
                    <td className="px-5 py-4 font-bold text-zafco-blue align-top">{row.region}</td>
                    <td className="px-5 py-4 text-gray-700 align-top">{row.industries}</td>
                    <td className="px-5 py-4 text-gray-700 align-top">{row.talent}</td>
                    <td className="px-5 py-4 text-gray-700 align-top">{row.capability}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── SECTIONS 4-7: Country-specific solution blocks ──────────────── */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="container mx-auto px-6 space-y-10">

          {/* 4 · Saudi Arabia */}
          <div className="rounded-2xl border border-slate-200 p-6 lg:p-10" data-testid="intl-saudi">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
              <div>
                <div className="text-[10px] font-bold tracking-[0.22em] text-green-700 uppercase mb-2">Saudi Arabia</div>
                <h2 className="text-2xl md:text-3xl font-bold text-zafco-blue font-montserrat leading-tight">
                  Recruitment solutions for Saudi Arabian employers
                </h2>
              </div>
              <Link to="/saudi-arabia" className="text-sm font-semibold text-zafco-blue hover:text-amber-600 inline-flex items-center gap-1 shrink-0">
                Detailed Saudi services <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <p className="text-gray-700 leading-relaxed mb-5">
              From mega-EPC construction projects to oil-and-gas turnaround crews to five-star hospitality
              mobilizations, Saudi Arabia is one of our longest-running corridors. We handle MoFA attestation,
              Saudi Wakala services, employment visa stamping and pre-departure orientation entirely in-house at
              our Delhi office — reducing your time-to-deploy and your administrative load.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {['Construction & Infrastructure','Oil & Gas / Energy','Hospitality','Healthcare','Industrial Projects','Logistics & Warehousing','Retail','Facilities Management'].map(t => (
                <div key={t} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" aria-hidden="true" /> {t}
                </div>
              ))}
            </div>
          </div>

          {/* 5 · Europe */}
          <div className="rounded-2xl border border-slate-200 p-6 lg:p-10" data-testid="intl-europe">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
              <div>
                <div className="text-[10px] font-bold tracking-[0.22em] text-blue-700 uppercase mb-2">European Union</div>
                <h2 className="text-2xl md:text-3xl font-bold text-zafco-blue font-montserrat leading-tight">
                  Recruitment solutions for European employers
                </h2>
              </div>
              <Link to="/germany-opportunity-card" className="text-sm font-semibold text-zafco-blue hover:text-amber-600 inline-flex items-center gap-1 shrink-0">
                Germany Opportunity Card <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <p className="text-gray-700 leading-relaxed mb-5">
              Across Germany, Poland, Romania, Hungary and the Czech Republic we partner with manufacturers,
              logistics operators, engineering firms and hospitality groups looking for English-capable,
              ITI- or NSDC-certified Indian workforce. Our compliance desk aligns every deployment to host-country
              labour-code requirements — work-permit type, salary thresholds, sectoral attestations, A1 / posted-worker
              status where applicable.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {['Manufacturing','Engineering','Logistics & Warehousing','Construction','Hospitality','Skilled Trades','Drivers (CE / C+E)','Healthcare Support'].map(t => (
                <div key={t} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" aria-hidden="true" /> {t}
                </div>
              ))}
            </div>
          </div>

          {/* 6 · Russia */}
          <div className="rounded-2xl border border-slate-200 p-6 lg:p-10" data-testid="intl-russia">
            <div>
              <div className="text-[10px] font-bold tracking-[0.22em] text-red-700 uppercase mb-2">Russia</div>
              <h2 className="text-2xl md:text-3xl font-bold text-zafco-blue font-montserrat leading-tight mb-4">
                Recruitment solutions for Russian industrial &amp; infrastructure projects
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-5">
              Russia is an active corridor for long-cycle industrial mandates — plant erection, infrastructure
              construction, energy projects, manufacturing scale-ups. We deploy project technicians, mechanical and
              electrical specialists, plant operators and skilled fabrication crews on project-bound contracts, with
              full-cycle visa, mobilization and welfare support, and the operational flexibility to rotate manpower
              across CIS sites where clients have multi-country footprints. For detailed pricing, role coverage and the
              2025–26 visa pathway, see our dedicated{' '}
              <Link
                to="/russia-recruitment-agency-india"
                className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-red-700 transition-colors"
                data-testid="intl-russia-link-to-dedicated-page"
              >
                Russia Recruitment Agency in India
              </Link>
              {' '}page.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {['Industrial Projects','Infrastructure','Manufacturing','Energy','Pipeline Construction','Heavy Equipment Operators','Skilled Technical Workforce','Supervisory Staff'].map(t => (
                <div key={t} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" aria-hidden="true" /> {t}
                </div>
              ))}
            </div>
          </div>

          {/* 7 · Israel */}
          <div className="rounded-2xl border border-slate-200 p-6 lg:p-10" data-testid="intl-israel">
            <div>
              <div className="text-[10px] font-bold tracking-[0.22em] text-indigo-700 uppercase mb-2">Israel</div>
              <h2 className="text-2xl md:text-3xl font-bold text-zafco-blue font-montserrat leading-tight mb-4">
                Construction-trade recruitment for Israeli employers
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-5">
              For Israel we specialise in the construction-trade corridor — iron-benders, shutter carpenters,
              plasterers, tile masons and formwork crews. Our recruitment workflow is aligned to PIBA (Population
              and Immigration Authority) documentation requirements and every shortlisted candidate clears a
              practical trade test conducted by a qualified assessor before being put forward for client interview.
              We coordinate visa documentation, departure logistics and on-arrival employer handover.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {['Construction Manpower','Infrastructure Development','Specialized Technical Services','Skilled Workforce Deployment','Iron-Bending Crews','Shutter Carpentry','Plastering & Finishing','Tile-Mason Teams'].map(t => (
                <div key={t} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" aria-hidden="true" /> {t}
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ── SECTION 8: Specialized Industry Verticals ───────────────────── */}
      <section className="py-14 lg:py-20 bg-slate-50" data-testid="intl-verticals">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mb-9">
            <div className="text-[11px] font-bold tracking-[0.22em] text-zafco-blue uppercase mb-3">Industry Verticals</div>
            <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mb-3 leading-tight">
              Specialised deployment expertise — six core verticals
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Vertical-specialist desks. Trade-test infrastructure tuned to each industry. Sourcing pipelines
              that draw from India\'s most relevant skilling clusters.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {VERTICALS.map((v) => (
              <div key={v.title} className="rounded-xl border border-slate-200 bg-white p-5 hover:border-amber-300 hover:shadow-md transition">
                <v.icon className="w-7 h-7 text-amber-600 mb-3" aria-hidden="true" />
                <h3 className="font-bold text-zafco-blue mb-2">{v.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 9: Dual Value Proposition ───────────────────────────── */}
      <section className="py-14 lg:py-20 bg-white" data-testid="intl-value-props">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-10">
          {/* Employers */}
          <div>
            <div className="text-[11px] font-bold tracking-[0.22em] text-zafco-blue uppercase mb-3">For Global Employers</div>
            <h2 className="text-2xl md:text-3xl font-bold text-zafco-blue font-montserrat mb-5 leading-tight">
              Six-step end-to-end deployment, accountable at every stage
            </h2>
            <ol className="space-y-3">
              {EMPLOYER_PROCESS.map((s, i) => (
                <li key={s.title} className="flex gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50/40">
                  <div className="w-9 h-9 rounded-full bg-zafco-blue text-white flex items-center justify-center font-bold shrink-0">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div>
                    <div className="font-bold text-zafco-blue text-sm md:text-base">{s.title}</div>
                    <div className="text-sm text-gray-600 leading-relaxed mt-0.5">{s.desc}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Candidates */}
          <div>
            <div className="text-[11px] font-bold tracking-[0.22em] text-amber-600 uppercase mb-3">For Job Seekers</div>
            <h2 className="text-2xl md:text-3xl font-bold text-zafco-blue font-montserrat mb-5 leading-tight">
              Ethical, transparent, employer-paid recruitment
            </h2>
            <div className="space-y-3">
              {CANDIDATE_PROMISES.map((p) => (
                <div key={p} className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50/60">
                  <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
                  <span className="text-sm text-gray-800 leading-relaxed">{p}</span>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <Button asChild className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                <Link to="/careers" data-testid="intl-candidate-cta">
                  Explore active overseas openings <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 10: Proof of Authenticity ───────────────────────────── */}
      <section className="py-14 lg:py-20 bg-zafco-blue text-white" data-testid="intl-proof">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mb-9">
            <div className="text-[11px] font-bold tracking-[0.22em] text-amber-300 uppercase mb-3">Operational Excellence</div>
            <h2 className="text-3xl md:text-4xl font-bold font-montserrat mb-3 leading-tight">
              Why serious international employers partner with Zafco HRM
            </h2>
            <p className="text-slate-300 leading-relaxed">
              Compliance, scale, ethics and operational depth — built over a decade and demonstrable on every mandate.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {TRUST_BULLETS.map((t) => (
              <div key={t.label} className="rounded-xl bg-white/8 border border-white/15 p-5 backdrop-blur-sm">
                <t.icon className="w-7 h-7 text-amber-300 mb-3" aria-hidden="true" />
                <div className="font-bold mb-1">{t.label}</div>
                <div className="text-sm text-slate-300 leading-relaxed">{t.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 11: FAQ ─────────────────────────────────────────────── */}
      <section className="py-14 lg:py-20 bg-white" data-testid="intl-faq">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mb-9">
            <div className="text-[11px] font-bold tracking-[0.22em] text-zafco-blue uppercase mb-3">FAQ</div>
            <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mb-3 leading-tight">
              Frequently asked questions
            </h2>
          </div>
          <div className="max-w-4xl space-y-3">
            {FAQS.map((f, i) => (
              <details key={f.q} className="group rounded-xl border border-slate-200 bg-slate-50/50 px-5 py-4 open:bg-white open:shadow-sm">
                <summary className="cursor-pointer font-semibold text-zafco-blue list-none flex items-start justify-between gap-3">
                  <span><span className="text-amber-600 mr-2">{String(i + 1).padStart(2, '0')}.</span>{f.q}</span>
                  <ChevronRight className="w-5 h-5 shrink-0 transition-transform group-open:rotate-90 text-slate-400" aria-hidden="true" />
                </summary>
                <p className="mt-3 text-sm md:text-[15px] text-gray-700 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 12: Final dual CTA ──────────────────────────────────── */}
      <section className="py-14 lg:py-20 bg-gradient-to-br from-zafco-blue via-slate-900 to-zafco-blue text-white" data-testid="intl-final-cta">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-8">
          <div className="rounded-2xl bg-white/8 border border-white/15 p-7 backdrop-blur-sm">
            <Briefcase className="w-9 h-9 text-amber-300 mb-3" aria-hidden="true" />
            <h3 className="text-2xl font-bold font-montserrat mb-3">For Global Employers</h3>
            <p className="text-slate-300 mb-5 leading-relaxed text-sm md:text-base">
              Share your manpower indent. Our Global Recruitment Desk will revert with a sourcing brief,
              timeline and indicative commercials within one working day.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                <Link to="/contact?source=international-recruitment-pillar-employer" data-testid="intl-final-employer-cta">
                  Talk to our Employer Desk <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <a href={`tel:${PRIMARY_PHONE.replace(/[^+0-9]/g,'')}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 hover:bg-white/15 transition text-sm font-semibold">
                <Phone className="w-4 h-4" /> {PRIMARY_PHONE}
              </a>
            </div>
          </div>
          <div className="rounded-2xl bg-white/8 border border-white/15 p-7 backdrop-blur-sm">
            <Users className="w-9 h-9 text-amber-300 mb-3" aria-hidden="true" />
            <h3 className="text-2xl font-bold font-montserrat mb-3">For Candidates</h3>
            <p className="text-slate-300 mb-5 leading-relaxed text-sm md:text-base">
              Send us your profile. We will assess fitment against active openings and revert only with
              authentic, employer-paid opportunities. No placement fees — ever.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                <Link to="/careers?source=international-recruitment-pillar-candidate" data-testid="intl-final-candidate-cta">
                  Submit your profile <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <a href={`mailto:${PRIMARY_EMAIL}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 hover:bg-white/15 transition text-sm font-semibold">
                <Mail className="w-4 h-4" /> {PRIMARY_EMAIL}
              </a>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-6 mt-9 text-center text-slate-400 text-xs">
          <MapPin className="inline-block w-3.5 h-3.5 mr-1.5 align-text-bottom" />
          Zafco Human Resource Management · New Delhi, India · Government of India Approved (MEA Reg. {MEA_REG})
        </div>
      </section>
    </div>
  );
};

export default InternationalManpowerRecruitmentPage;
